import * as React from 'react';
import { useIntl } from 'react-intl';
import { CmdCalendar } from '@commander-services/gui-components';
import '../Forms/CmdCalendar/calendar.scss';
import dayjs from 'dayjs';
import {
  calendarLanguage,
  calendarRangesTranslations,
  calendarTranslations,
} from '../Forms/ReactHookFormFields/Calendar';

interface IComponentProps {
  name: string;
  type: string;
  onChangeCallback: (dates?: Date[] | null) => void;
  defaultDates?: Date[];
  onlyOneDay?: boolean;
  rideChoosing?: boolean;
}

export default function TableDatepicker(props: IComponentProps) {
  const getStartDate = (date: Date) => {
    return new Date(date.setHours(0, 0, 0, 0));
  };

  const getEndDate = (date: Date) => {
    const today = new Date();
    if (
      props.rideChoosing &&
      today.getDate() === date.getDate() &&
      today.getMonth() === date.getMonth() &&
      today.getFullYear() === date.getFullYear()
    ) {
      return new Date();
    }
    return new Date(date.setHours(23, 59, 59, 999));
  };

  const { formatMessage: f } = useIntl();
  const [dateValue, setDateValue] = React.useState<Date[]>(
    props.defaultDates || [getStartDate(new Date()), getEndDate(new Date())]
  );

  const getAllowedRange = () => {
    const today = new Date();
    const twoYearsAgo = new Date(today.setFullYear(today.getFullYear() - 2));
    return [twoYearsAgo, new Date()];
  };

  const getRefDate = (date: Date) => {
    let refDate = new Date();
    const newDate = dayjs(new Date(date))
      .set('second', 0)
      .set('millisecond', 0)
      .set('minute', 0)
      .set('hour', 24)
      .toDate();
    refDate = new Date(
      props.onlyOneDay
        ? newDate.setDate(newDate.getDate() - 1)
        : newDate.setMonth(newDate.getMonth() - 1)
    );
    return refDate;
  };

  React.useEffect(() => {
    if (props.defaultDates && props.defaultDates.length === 2) {
      setDateValue(props.defaultDates);
    }
  }, [props.defaultDates]);

  const getRangeValidity = (fromDate: Date, toDate: Date) => {
    if (getRefDate(toDate).getTime() > new Date(fromDate).getTime()) {
      return false;
    }
    return true;
  };

  const getTimeValidity = (fromDate: Date, toDate: Date) => {
    if (new Date(fromDate).getTime() > new Date(toDate).getTime()) {
      return false;
    }
    return true;
  };

  const changeCalendarValue = (value: Date[]) => {
    if (value && value.length === 2) {
      if (!getTimeValidity(value[0], value[1])) {
        props.onChangeCallback(null);
      } else if (!getRangeValidity(value[0], value[1])) {
        props.onChangeCallback();
      } else if (!props.rideChoosing) {
        setDateValue(value);
        props.onChangeCallback(value);
      } else {
        const newValue = [getStartDate(value[0]), getEndDate(value[1])];
        setDateValue(newValue);
        props.onChangeCallback(newValue);
      }
    }
  };

  const getDatesChip = () => {
    return (
      <a
        href="#"
        // title={f({ id: 'table.filter.button' })}
        data-cy="datapicker-filter-graphs"
        className="dropdown-toggle text-white text-decoration-none"
        // id="dropdownMenu13"
        data-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="true"
        // onClick={actionOpen}
        style={{ paddingRight: '0' }}
      >
        {f({ id: 'onlinepanel.date' })}: {dayjs(dateValue[0]).format('DD.MM.YYYY HH:mm')} -{' '}
        {dayjs(dateValue[1]).format('DD.MM.YYYY HH:mm')}
      </a>
    );
  };

  return (
    <div
      id={`tableDatepicker-${props.name}`}
      className="input-actions flatpickr-inline"
      role="presentation"
    >
      <CmdCalendar
        id={`tableDatepicker-${props.name}-${props.type}`}
        mode="filter"
        value={dateValue}
        initialValue={props.rideChoosing ? undefined : dateValue}
        onChangeCallback={changeCalendarValue}
        isToday24h
        disabledItems={
          props.onlyOneDay
            ? ['LAST_WEEK', 'THIS_MONTH', 'PREVIOUS_MONTH', 'THIS_YEAR', 'PREVIOUS_YEAR']
            : ['THIS_YEAR', 'PREVIOUS_YEAR']
        }
        language={calendarLanguage}
        rangesTranslations={calendarRangesTranslations(f)}
        calendarTranslations={calendarTranslations(f)}
        allowedRange={getAllowedRange()}
        dateFormat={props.rideChoosing ? undefined : 'dd.MM.yyyy HH:mm'}
      >
        {getDatesChip()}
      </CmdCalendar>
    </div>
  );
}
